import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Menu,
  X,
  Search,
  Radio,
  ChevronDown,
  User2,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
} from "lucide-react";
import { useAuth } from "@/features/auth/AuthProvider";
import { ClubLogo } from "@/components/brand/ClubLogo";
import { GlobalSearch, useGlobalSearch } from "@/components/search/GlobalSearch";
import { Container } from "@/components/ui/section";
import { LiveBadge } from "@/components/ui/live-badge";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { publicService } from "@/services/publicService";
import { qk } from "@/api/queryKeys";
import { format } from "date-fns";
import { useSiteContent } from "@/lib/site-content";
import { RichText } from "@/components/content/RichText";
import { toast } from "sonner";

const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/sowetosuperunitedfc/",
  instagram: "https://www.facebook.com/sowetosuperunitedfc/",
  twitter: "https://www.facebook.com/sowetosuperunitedfc/",
  youtube: "https://www.youtube.com/@SowetoSuperUnitedFC",
} as const;

const NAV: Array<{ label: string; to: string; children?: Array<{ label: string; to: string }> }> = [
  { label: "News", to: "/news" },
  { label: "Fixtures", to: "/fixtures" },
  { label: "Results", to: "/results" },
  { label: "Competitions", to: "/competitions" },
  {
    label: "Teams",
    to: "/teams",
    children: [
      { label: "First Team", to: "/teams/senior-men" },
      { label: "Technical Team", to: "/staff" },
    ],
  },
  { label: "Watch Live", to: "/watch" },
  { label: "Videos", to: "/videos" },
  { label: "Galleries", to: "/galleries" },
  {
    label: "Club",
    to: "/about",
    children: [
      { label: "About", to: "/about" },
      { label: "History", to: "/about/history" },
      { label: "Mission, Vision & Values", to: "/about/mission-vision-values" },
      { label: "Leadership", to: "/about/leadership" },
      { label: "Sponsors", to: "/sponsors" },
      { label: "Contact", to: "/contact" },
    ],
  },
];

export function PublicLayout() {
  const { content } = useSiteContent();
  const { user, status } = useAuth();
  const search = useGlobalSearch();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [portalUnlocked, setPortalUnlocked] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("portal-unlocked") === "true",
  );
  const rightsClickCount = useRef(0);
  const mobileScrollPosition = useRef(0);
  const location = useLocation();
  const locationKey = `${location.pathname}${location.search}`;
  const locationKeyRef = useRef(locationKey);
  locationKeyRef.current = locationKey;
  const isHome = location.pathname === "/";

  const nextMatch = useQuery({
    queryKey: qk.public.liveMatch,
    queryFn: publicService.liveMatch,
    staleTime: 60_000,
  });
  const streams = useQuery({
    queryKey: qk.public.streams({ perPage: 1 }),
    queryFn: () => publicService.streams({ perPage: 1 }),
  });
  const hasLiveStream = streams.data?.data.some((s) => s.status === "live") ?? false;

  useEffect(() => {
    document.querySelector('meta[name="robots"]')?.remove();
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);
  useEffect(() => {
    if (!mobileOpen) return;
    const openedAt = locationKeyRef.current;
    const scrollY = mobileScrollPosition.current;
    const bodyOverflow = document.body.style.overflow;
    const bodyPosition = document.body.style.position;
    const bodyTop = document.body.style.top;
    const bodyWidth = document.body.style.width;
    const htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = bodyOverflow;
      document.body.style.position = bodyPosition;
      document.body.style.top = bodyTop;
      document.body.style.width = bodyWidth;
      document.documentElement.style.overflow = htmlOverflow;
      window.scrollTo({
        top: locationKeyRef.current === openedAt ? scrollY : 0,
        left: 0,
        behavior: "auto",
      });
    };
  }, [mobileOpen]);

  const headerSolid = !isHome || scrolled || mobileOpen;
  const unlockPortal = () => {
    if (portalUnlocked) return;
    rightsClickCount.current += 1;
    if (rightsClickCount.current >= 7) {
      sessionStorage.setItem("portal-unlocked", "true");
      setPortalUnlocked(true);
    }
  };

  return (
    <div className="flex min-h-dvh w-full max-w-full flex-col overflow-x-clip bg-background text-foreground">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-gold focus:px-4 focus:py-2 focus:text-navy"
      >
        Skip to main content
      </a>
      <GlobalSearch />

      {/* Utility bar */}
      <div className="hidden bg-navy-deep text-silver-light md:block">
        <Container className="flex h-9 items-center justify-between text-[11px] font-medium">
          <p className="uppercase tracking-[0.18em]">Izinj Ezistrong · Top Dogs</p>
          <div className="flex items-center gap-4">
            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              className="hover:text-gold"
            >
              <Facebook aria-hidden className="size-3.5" />
            </a>
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="hover:text-gold"
            >
              <Instagram aria-hidden className="size-3.5" />
            </a>
            <a
              href={SOCIAL_LINKS.twitter}
              target="_blank"
              rel="noreferrer"
              aria-label="Twitter"
              className="hover:text-gold"
            >
              <Twitter aria-hidden className="size-3.5" />
            </a>
            <a
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube"
              className="hover:text-gold"
            >
              <Youtube aria-hidden className="size-3.5" />
            </a>
            <span className="mx-2 h-3 w-px bg-white/20" />
            {portalUnlocked ? (
              status === "authenticated" && user ? (
                <PortalLink user={user} />
              ) : (
                <Link to="/auth/login" className="inline-flex items-center gap-1 hover:text-gold">
                  <User2 aria-hidden className="size-3.5" /> Portal login
                </Link>
              )
            ) : null}
          </div>
        </Container>
      </div>

      <header
        className={cn(
          "sticky top-0 z-50 transition-colors duration-300",
          headerSolid ? "bg-navy text-white shadow-editorial" : "bg-transparent text-white",
        )}
      >
        <Container className="flex h-16 items-center gap-3 lg:h-20">
          <Link
            to="/"
            className="flex items-center gap-2"
            aria-label="Soweto Super United FC — home"
          >
            <ClubLogo variant="icon" priority className="h-11 w-11 sm:h-12 sm:w-12" />
            <span className="hidden text-display text-lg font-bold uppercase leading-none tracking-wide md:block">
              Soweto<span className="text-gold"> Super</span> United
              <span className="sr-only"> FC</span>
            </span>
          </Link>

          <nav aria-label="Primary" className="ml-6 hidden lg:block">
            <ul className="flex items-center gap-1">
              {NAV.map((item) => (
                <li key={item.to} className="group relative">
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      cn(
                        "inline-flex items-center gap-1 px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors hover:text-gold",
                        isActive && "text-gold",
                      )
                    }
                  >
                    {item.label}
                    {item.children ? <ChevronDown aria-hidden className="size-3" /> : null}
                  </NavLink>
                  {item.children ? (
                    <div className="invisible absolute left-0 top-full min-w-[220px] translate-y-1 rounded-md border border-white/10 bg-navy-deep p-2 opacity-0 shadow-editorial transition group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block rounded-sm px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-silver-light hover:bg-white/5 hover:text-gold"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              aria-label="Search (⌘K)"
              onClick={search.open}
              className="hidden size-11 place-items-center rounded-sm hover:bg-white/10 md:grid"
            >
              <Search aria-hidden className="size-4" />
            </button>
            <Link
              to="/watch"
              className={cn(
                "hidden items-center gap-2 rounded-sm px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] transition sm:inline-flex",
                hasLiveStream
                  ? "bg-live text-white hover:opacity-90"
                  : "border border-gold text-gold hover:bg-gold hover:text-navy",
              )}
            >
              {hasLiveStream ? (
                <LiveBadge label="Watch Live" className="bg-transparent p-0" />
              ) : (
                <>
                  <Radio aria-hidden className="size-3.5" /> Watch Live
                </>
              )}
            </Link>
            {nextMatch.data ? (
              <Link
                to={`/matches/${nextMatch.data.publicId}`}
                className="hidden rounded-sm border border-white/20 px-3 py-2 text-left text-[11px] uppercase tracking-[0.12em] hover:bg-white/10 lg:block"
              >
                <p className="text-[9px] text-silver">
                  {nextMatch.data.status === "live" ? "Live now" : "Next match"}
                </p>
                <p className="text-display text-sm font-bold leading-none text-white">
                  vs{" "}
                  {(nextMatch.data.homeTeam.name.includes("Soweto")
                    ? nextMatch.data.awayTeam.name
                    : nextMatch.data.homeTeam.name
                  ).replace(" FC", "")}
                </p>
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => {
                if (!mobileOpen) mobileScrollPosition.current = window.scrollY;
                setMobileOpen((open) => !open);
              }}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              className="grid size-11 place-items-center rounded-sm hover:bg-white/10 lg:hidden"
            >
              {mobileOpen ? <X aria-hidden /> : <Menu aria-hidden />}
            </button>
          </div>
        </Container>
        {/* Mobile nav — full-screen navy panel */}
        {mobileOpen ? (
          <div
            id="mobile-nav"
            className="fixed inset-x-0 top-16 z-40 h-[calc(100dvh-4rem)] touch-pan-y overflow-y-auto overscroll-y-contain bg-navy-deep lg:hidden"
          >
            <Container className="py-4">
              <nav aria-label="Primary mobile">
                <ul className="divide-y divide-white/10">
                  {NAV.map((item) => (
                    <li key={item.to} className="py-1">
                      <Link
                        to={item.to}
                        className="flex items-center justify-between py-3 text-display text-xl font-bold uppercase tracking-wide text-white hover:text-gold"
                      >
                        {item.label}
                        <ChevronDown aria-hidden className="size-4 -rotate-90 text-gold" />
                      </Link>
                      {item.children ? (
                        <ul className="mb-2 grid gap-1 pl-2">
                          {item.children.map((c) => (
                            <li key={c.to}>
                              <Link
                                to={c.to}
                                className="block py-1.5 text-sm text-silver-light hover:text-gold"
                              >
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="mt-6 grid gap-3">
                <Link
                  to="/watch"
                  className="inline-flex items-center justify-center gap-2 rounded-sm bg-gold px-4 py-3 text-sm font-bold uppercase tracking-[0.18em] text-navy hover:opacity-90"
                >
                  <Radio aria-hidden className="size-4" /> Watch Live
                </Link>
                {portalUnlocked ? (
                  status === "authenticated" && user ? (
                    <PortalLink user={user} full />
                  ) : (
                    <Link
                      to="/auth/login"
                      className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/30 px-4 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-white hover:bg-white/10"
                    >
                      <User2 aria-hidden className="size-4" /> Portal login
                    </Link>
                  )
                ) : null}
                {nextMatch.data ? (
                  <Link
                    to={`/matches/${nextMatch.data.publicId}`}
                    className="rounded-md border border-white/10 bg-navy p-4 text-white"
                  >
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gold">
                      {nextMatch.data.status === "live" ? "Live now" : "Next match"}
                    </p>
                    <p className="mt-1 text-display text-lg font-bold">
                      {nextMatch.data.homeTeam.name.replace(" FC", "")} vs{" "}
                      {nextMatch.data.awayTeam.name.replace(" FC", "")}
                    </p>
                    <p className="text-xs text-silver">
                      {format(new Date(nextMatch.data.scheduledStart), "EEE d MMM · HH:mm")} ·{" "}
                      {nextMatch.data.venue}
                    </p>
                  </Link>
                ) : null}
              </div>
            </Container>
          </div>
        ) : null}
      </header>

      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      <Footer onRightsClick={unlockPortal} description={content.footer.description} />
    </div>
  );
}

function PortalLink({ user, full }: { user: { roles: string[] }; full?: boolean }) {
  const target =
    user.roles.includes("super_administrator") ||
    user.roles.includes("club_administrator") ||
    user.roles.includes("team_administrator")
      ? "/admin"
      : user.roles.includes("match_operator") || user.roles.includes("stream_operator")
        ? "/admin/streams"
        : user.roles.includes("head_coach") || user.roles.includes("assistant_coach")
          ? "/coach"
          : user.roles.includes("player")
            ? "/player"
            : "/";
  return (
    <Link
      to={target}
      className={cn(
        "inline-flex items-center gap-1 font-semibold uppercase tracking-[0.16em] text-gold hover:text-gold-light",
        full && "justify-center rounded-sm border border-gold px-4 py-3 text-sm text-gold",
      )}
    >
      <User2 aria-hidden className="size-3.5" /> My Portal
    </Link>
  );
}

function Footer({
  onRightsClick,
  description,
}: {
  onRightsClick: () => void;
  description: string;
}) {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterPending, setNewsletterPending] = useState(false);
  const subscribe = async () => {
    setNewsletterPending(true);
    try {
      await publicService.subscribeNewsletter(newsletterEmail);
      setNewsletterEmail("");
      toast.success("You are subscribed to club updates");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Subscription failed");
    } finally {
      setNewsletterPending(false);
    }
  };
  return (
    <footer className="bg-navy-deep text-silver-light">
      <div className="border-b border-white/10 bg-navy">
        <Container className="flex flex-col items-start gap-6 py-10 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-display text-3xl uppercase text-white">
              Join the Super United family
            </p>
            <p className="mt-1 text-sm text-silver">
              Fixtures, results, exclusive interviews and matchday news direct to your inbox.
            </p>
          </div>
          <form
            className="flex w-full max-w-md items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void subscribe();
            }}
          >
            <label htmlFor="footer-email" className="sr-only">
              Email
            </label>
            <input
              id="footer-email"
              type="email"
              required
              value={newsletterEmail}
              onChange={(event) => setNewsletterEmail(event.target.value)}
              placeholder="you@email.com"
              className="min-h-11 min-w-0 flex-1 rounded-sm border border-white/20 bg-white/5 px-3 text-sm text-white placeholder:text-silver focus:outline-none focus:ring-2 focus:ring-gold"
            />
            <button
              type="submit"
              disabled={newsletterPending}
              className="min-h-11 rounded-sm bg-gold px-4 text-sm font-bold uppercase tracking-[0.18em] text-navy hover:opacity-90"
            >
              {newsletterPending ? "Saving…" : "Subscribe"}
            </button>
          </form>
        </Container>
      </div>
      <Container className="grid gap-8 py-12 md:grid-cols-4">
        <div>
          <ClubLogo variant="full" className="h-16" />
          <RichText html={description} className="mt-3 text-sm text-silver" />
          <div className="mt-4 flex gap-3">
            <a
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              className="grid size-9 place-items-center rounded-sm border border-white/10 hover:border-gold hover:text-gold"
            >
              <Facebook aria-hidden className="size-4" />
            </a>
            <a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              className="grid size-9 place-items-center rounded-sm border border-white/10 hover:border-gold hover:text-gold"
            >
              <Instagram aria-hidden className="size-4" />
            </a>
            <a
              href={SOCIAL_LINKS.twitter}
              target="_blank"
              rel="noreferrer"
              aria-label="Twitter"
              className="grid size-9 place-items-center rounded-sm border border-white/10 hover:border-gold hover:text-gold"
            >
              <Twitter aria-hidden className="size-4" />
            </a>
            <a
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube"
              className="grid size-9 place-items-center rounded-sm border border-white/10 hover:border-gold hover:text-gold"
            >
              <Youtube aria-hidden className="size-4" />
            </a>
          </div>
        </div>
        <FooterCol
          title="Club"
          links={[
            { label: "About", to: "/about" },
            { label: "History", to: "/about/history" },
            { label: "Leadership", to: "/about/leadership" },
            { label: "Sponsors", to: "/sponsors" },
            { label: "Contact", to: "/contact" },
          ]}
        />
        <FooterCol
          title="Football"
          links={[
            { label: "Fixtures", to: "/fixtures" },
            { label: "Results", to: "/results" },
            { label: "League table", to: "/league-tables" },
            { label: "Competition history", to: "/competitions" },
            { label: "Teams", to: "/teams" },
            { label: "Watch Live", to: "/watch" },
          ]}
        />
        <FooterCol
          title="Fans"
          links={[
            { label: "News", to: "/news" },
            { label: "Videos", to: "/videos" },
            { label: "Galleries", to: "/galleries" },
            { label: "Sponsorship enquiry", to: "/forms/sponsorship" },
          ]}
        />
      </Container>
      <div className="border-t border-white/10">
        <Container className="flex flex-col gap-3 py-6 text-xs text-silver sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} Soweto Super United FC.{" "}
            <button type="button" onClick={onRightsClick} className="cursor-default">
              All rights reserved.
            </button>
          </p>
          <p>
            Made with Love ❤️{" "}
            <a href="https://ieosuia.com" className="underline hover:text-gold">
              IEOSUIA
            </a>
          </p>
        </Container>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: { label: string; to: string }[] }) {
  return (
    <div>
      <p className="text-display text-xs font-bold uppercase tracking-[0.24em] text-gold">
        {title}
      </p>
      <ul className="mt-3 grid gap-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-sm text-silver-light hover:text-gold">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
